import { AutomatedCarousel } from '@liveauctioneers/hammer-ui-core/automatedCarousel';
import { CAROUSEL_CARD_GAP_NUM, Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { ComponentProps, useMemo } from 'react';

import CarouselLoadingWrapper from '@/components/CarouselLoadingWrapper/CarouselLoadingWrapper';
import AutomatedCatalogCarouselCard from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/AutomatedCatalogCarouselCard';
import {
    AUTOMATED_CAROUSEL_CARD_OFFSET_MODIFIER,
    FEATURED_CATALOG_CARD_DESKTOP_WIDTH_FIT_BUFFER,
    FEATURED_CATALOG_CARD_MOBILE_PEEK_SIDES,
    FEATURED_CATALOG_CARD_MOBILE_SIZE_OFFSET_REDESIGN,
} from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/AutomatedCatalogCarouselCard.types';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { AmpProduct, useTrackAmpClicked } from '@/utils/amplitude/analytics';
import { getFeaturedCatalogsUrl } from '@/utils/urls';

import useGetFeaturedCatalogIds from './hooks/useGetFeaturedCatalogIds';

const DESKTOP_CARDS_PER_PAGE = 2;

type Props = {
    shouldUseHomePageRedesign?: boolean;
    width: number;
};

const FeaturedCatalogCarousel = ({ shouldUseHomePageRedesign = false, width }: Props) => {
    const isMobile = useIsMobileLayout();
    const { formatMessage } = useIntl();

    const catalogIds = useGetFeaturedCatalogIds();
    const trackAmpClicked = useTrackAmpClicked();

    const shouldShowTwoCardsPerPage = shouldUseHomePageRedesign && !isMobile && width > 0;
    const desktopCardWidth = shouldShowTwoCardsPerPage
        ? Math.floor((width - CAROUSEL_CARD_GAP_NUM) / DESKTOP_CARDS_PER_PAGE) -
          FEATURED_CATALOG_CARD_DESKTOP_WIDTH_FIT_BUFFER
        : undefined;

    const carouselCards = useMemo(() => {
        return catalogIds.map((catalogId) => ({
            card: (
                <AutomatedCatalogCarouselCard
                    cardWidth={desktopCardWidth}
                    catalogId={catalogId}
                    shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                    width={width}
                />
            ),
            key: `${catalogId}-featured`,
        }));
    }, [catalogIds, desktopCardWidth, shouldUseHomePageRedesign, width]);

    const title = useMemo(() => formatMessage({ id: 'featured_auctions' }), [formatMessage]);
    const action: ComponentProps<typeof Carousel>['action'] = useMemo(
        () => ({
            onClick: () => {
                trackAmpClicked({
                    ampProduct: AmpProduct.FeaturedAuctions,
                    catalogId: null,
                    houseId: null,
                });
            },
            path: getFeaturedCatalogsUrl(),
            text: formatMessage({
                id: 'viewAll',
            }),
        }),
        [formatMessage, trackAmpClicked]
    );

    if (!carouselCards?.length) {
        return null;
    }

    return (
        <CarouselLoadingWrapper id="featuredCatalog">
            <AutomatedCarousel
                action={action}
                cardOffset={
                    isMobile
                        ? shouldUseHomePageRedesign
                            ? FEATURED_CATALOG_CARD_MOBILE_SIZE_OFFSET_REDESIGN /
                              FEATURED_CATALOG_CARD_MOBILE_PEEK_SIDES
                            : width / AUTOMATED_CAROUSEL_CARD_OFFSET_MODIFIER
                        : 0
                }
                cards={carouselCards}
                expectedPageCount={
                    shouldShowTwoCardsPerPage
                        ? Math.ceil(carouselCards.length / DESKTOP_CARDS_PER_PAGE)
                        : // Probably "- 2" from the gaps and how they interact with page calculation in the carousel.¯\_(ツ)_/¯
                          carouselCards.length - 2
                }
                fullPage={!shouldShowTwoCardsPerPage}
                progressBarGap={isMobile ? undefined : 'sm'}
                title={title}
                useOverlayControls
            />
        </CarouselLoadingWrapper>
    );
};

export default FeaturedCatalogCarousel;
